import { KanbanColumn } from "./KanbanColumn";
import { ContactCard } from "./ContactCard";
import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ETAPAS, ETAPA_LABEL } from "@/lib/stages";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  pointerWithin, useDroppable,
} from "@dnd-kit/core";

export { ETAPAS };

const todayStr = () => {
  try { return new Date().toISOString().slice(0, 10); } catch { return ""; }
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

function DroppableTab({ etapaId, active, label, count, onClick, isMobile }) {
  const { setNodeRef, isOver } = useDroppable({ id: `tab-${etapaId}`, disabled: isMobile });
  return (
    <button
      type="button"
      ref={setNodeRef}
      data-testid={`etapa-tab-${etapaId}`}
      data-no-drag
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-all duration-200 whitespace-nowrap ${
        active
          ? "bg-amber-500 text-zinc-950 font-semibold shadow-sm"
          : isOver
            ? "bg-[var(--salonup-brand-alpha-14)] text-[var(--salonup-brand)] ring-1 ring-[var(--salonup-brand)]"
            : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/70"
      }`}
    >
      <span>{label}</span>
      <span className={`inline-flex items-center justify-center min-w-[20px] h-[18px] rounded px-1 text-[10px] tabular-nums ${
        active ? "bg-zinc-950/20 text-zinc-950" : "bg-zinc-800 text-zinc-400"
      }`}>{count}</span>
    </button>
  );
}

export function KanbanBoard({ contacts, onEdit, onWhatsApp, onNote, onMove, onSchedule, onDelete, cardParadoDias = 3, activeCardId = null }) {
  const scrollRef = useRef(null);
  const colRefs = useRef({});
  const [activeEtapa, setActiveEtapa] = useState(ETAPAS[0].id);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const isMobile = useIsMobile();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const grouped = useMemo(() => {
    const map = {};
    ETAPAS.forEach((e) => { map[e.id] = []; });
    contacts.forEach((c) => {
      if (map[c.etapa]) map[c.etapa].push(c);
    });
    const today = todayStr();
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const aOver = a.retornar_em && a.retornar_em <= today ? 0 : 1;
        const bOver = b.retornar_em && b.retornar_em <= today ? 0 : 1;
        if (aOver !== bOver) return aOver - bOver;
        return 0;
      });
    });
    return map;
  }, [contacts]);

  const counts = useMemo(() => {
    const r = {};
    ETAPAS.forEach((e) => { r[e.id] = grouped[e.id].length; });
    return r;
  }, [grouped]);

  const updateNav = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    const containerLeft = el.getBoundingClientRect().left;
    let bestId = ETAPAS[0].id;
    let bestDist = Infinity;
    ETAPAS.forEach((e) => {
      const node = colRefs.current[e.id];
      if (node) {
        const d = Math.abs(node.getBoundingClientRect().left - containerLeft);
        if (d < bestDist) { bestDist = d; bestId = e.id; }
      }
    });
    setActiveEtapa(bestId);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateNav();
    el.addEventListener("scroll", updateNav, { passive: true });
    window.addEventListener("resize", updateNav);
    return () => {
      el.removeEventListener("scroll", updateNav);
      window.removeEventListener("resize", updateNav);
    };
  }, [updateNav, contacts]);

  const scrollToEtapa = useCallback((etapaId) => {
    const el = scrollRef.current;
    const node = colRefs.current[etapaId];
    if (!el || !node) return;
    const left = node.offsetLeft - (isMobile ? 0 : 24);
    el.scrollTo({ left, behavior: "smooth" });
  }, [isMobile]);

  const scrollByColumn = useCallback((dir) => {
    const idx = ETAPAS.findIndex((e) => e.id === activeEtapa);
    const next = Math.max(0, Math.min(ETAPAS.length - 1, idx + dir));
    scrollToEtapa(ETAPAS[next].id);
  }, [activeEtapa, scrollToEtapa]);

  // --- Pan-scroll (arrastar o fundo) ---
  const dragState = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const onPointerDown = (e) => {
    if (isMobile) return;
    if (e.target && e.target.closest("[data-dnd-handle]")) return; // drag de card tem precedência
    if (e.target && e.target.closest("[data-no-drag]")) return;
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
    el.classList.add("kanban-dragging");
  };
  const onPointerMove = (e) => {
    const s = dragState.current;
    if (!s.down) return;
    const el = scrollRef.current;
    if (!el) return;
    const dx = e.clientX - s.startX;
    if (Math.abs(dx) > 3) s.moved = true;
    el.scrollLeft = s.startScroll - dx;
  };
  const onPointerUp = () => {
    const el = scrollRef.current;
    if (el) el.classList.remove("kanban-dragging");
    if (dragState.current.moved && el) {
      el.setAttribute("data-just-dragged", "1");
      setTimeout(() => el.removeAttribute("data-just-dragged"), 150);
    }
    dragState.current.down = false;
  };

  // --- DnD-kit handlers ---
  const activeContact = useMemo(() => contacts.find((c) => c.id === activeId) || null, [contacts, activeId]);
  const handleDragStart = (e) => setActiveId(e.active?.id || null);
  const handleDragEnd = (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const target = String(over.id);
    let etapaId = null;
    if (target.startsWith("col-")) etapaId = target.slice(4);
    else if (target.startsWith("tab-")) etapaId = target.slice(4);
    if (!etapaId) return;
    const contact = contacts.find((c) => c.id === active.id);
    if (!contact || contact.etapa === etapaId) return;
    onMove(contact, etapaId);
  };
  const handleDragCancel = () => setActiveId(null);

  const board = (
    <div className="relative flex flex-col h-full" data-testid="kanban-board">
      {/* Barra de etapas (desktop + mobile como abas) */}
      <div className="flex-shrink-0 px-6 pb-3 overflow-x-auto scrollbar-hide" data-testid="etapas-bar">
        <div className="inline-flex items-center gap-1.5 min-w-max bg-zinc-900/40 rounded-lg p-1">
          {ETAPAS.map((e) => (
            <DroppableTab
              key={e.id}
              etapaId={e.id}
              active={e.id === activeEtapa}
              label={e.label}
              count={counts[e.id]}
              onClick={() => scrollToEtapa(e.id)}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>

      {/* Área do quadro com setas laterais */}
      <div className="relative flex-1 min-h-0">
        {!isMobile && canLeft && (
          <button
            data-no-drag
            data-testid="kanban-arrow-left"
            onClick={() => scrollByColumn(-1)}
            aria-label="Coluna anterior"
            className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-zinc-800/90 border border-zinc-700 text-zinc-100 hover:bg-amber-500 hover:text-zinc-950 hover:border-amber-500 transition-colors shadow-lg"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {!isMobile && canRight && (
          <button
            data-no-drag
            data-testid="kanban-arrow-right"
            onClick={() => scrollByColumn(1)}
            aria-label="Próxima coluna"
            className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-zinc-800/90 border border-zinc-700 text-zinc-100 hover:bg-amber-500 hover:text-zinc-950 hover:border-amber-500 transition-colors shadow-lg"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        <div
          ref={scrollRef}
          className={`kanban-scroll ${isMobile ? "kanban-snap" : ""} h-full overflow-x-auto overflow-y-hidden flex gap-8 flex-nowrap px-6 pb-4 ${isMobile ? "" : "cursor-grab"}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onPointerCancel={onPointerUp}
          onClickCapture={(e) => {
            const el = scrollRef.current;
            if (el && el.getAttribute("data-just-dragged") === "1") {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
        >
          {ETAPAS.map((etapa) => (
            <div
              key={etapa.id}
              ref={(el) => (colRefs.current[etapa.id] = el)}
              className={isMobile ? "flex-shrink-0 w-full" : ""}
            >
              <KanbanColumn
                etapa={etapa}
                contacts={grouped[etapa.id]}
                onEdit={onEdit}
                onWhatsApp={onWhatsApp}
                onNote={onNote}
                onMove={onMove}
                onSchedule={onSchedule}
                onDelete={onDelete}
                cardParadoDias={cardParadoDias}
                isMobile={isMobile}
                activeId={activeId || activeCardId}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (isMobile) return board;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {board}
      <DragOverlay dropAnimation={null}>
        {activeContact ? (
          <div style={{ width: 320, transform: "rotate(2deg)", opacity: 0.95, boxShadow: "0 20px 40px rgba(0,0,0,0.18)" }} className="pointer-events-none">
            <ContactCard
              contact={activeContact}
              etapa={activeContact.etapa}
              onEdit={() => {}}
              onWhatsApp={() => {}}
              onNote={() => {}}
              onMove={() => {}}
              onSchedule={() => {}}
              onDelete={() => {}}
              cardParadoDias={cardParadoDias}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
